import { useEffect, useState } from "react";
import { Download, KeyRound, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { api, errorText } from "@/lib/api";
import { TextField } from "@/components/tools/fields";

const Card = ({ title, children, testid }) => (
  <section className="rounded-2xl border-2 border-line bg-surface p-5 sm:p-6" data-testid={testid}>
    <h2 className="mb-4 font-display text-2xl font-medium">{title}</h2>
    {children}
  </section>
);

const useGet = (url, fallback) => {
  const [data, setData] = useState(fallback);
  useEffect(() => {
    api.get(url).then((r) => setData(r.data ?? fallback)).catch(() => setData(fallback));
  }, [url]); // eslint-disable-line react-hooks/exhaustive-deps
  return data;
};

const Downloads = () => {
  const d = useGet("/admin/downloads", { resources: [], total: 0, unique_visitors: 0 });
  return (
    <Card title="Downloads" testid="admin-downloads">
      <p className="mb-3 text-sm text-ink-soft">{d.total ?? 0} downloads · {d.unique_visitors ?? 0} unique visitors (anonymous)</p>
      <ul className="space-y-1.5 text-sm">
        {(d.resources || []).map((r) => <li key={r.resource} className="flex justify-between gap-3"><span className="font-bold">{r.resource}</span><span>{r.total} total · {r.last_30_days} in 30 days</span></li>)}
        {!d.resources?.length && <li className="text-ink-soft">No downloads yet.</li>}
      </ul>
    </Card>
  );
};

const Security = () => {
  const d = useGet("/admin/security", { alerts: [], events: [] });
  return (
    <Card title="Security events" testid="admin-security">
      {(d.alerts || []).map((a) => <p key={a} role="alert" className="mb-2 flex items-center gap-2 rounded-xl p-3 text-sm font-bold" style={{ background: "var(--rag-red)", color: "var(--rag-red-fg)" }}><AlertTriangle size={16} aria-hidden />{a}</p>)}
      {!d.alerts?.length && <p className="mb-3 text-sm font-bold" data-testid="admin-no-alerts">No alerts in the last 24 hours.</p>}
      <ul className="max-h-60 space-y-1 overflow-auto text-xs" data-lenis-prevent>
        {(d.events || []).slice(0, 60).map((e, i) => <li key={`${e.timestamp}-${i}`} className="flex justify-between gap-3 border-b border-line py-1"><span className="font-bold">{e.event}</span><span className="text-ink-soft">{new Date(e.timestamp).toLocaleString("en-GB")} · {e.ip}</span></li>)}
      </ul>
    </Card>
  );
};

const Schools = () => {
  const list = useGet("/admin/schools", []);
  return (
    <Card title={`School workspaces (${Array.isArray(list) ? list.length : 0})`} testid="admin-schools">
      <p className="mb-3 text-sm text-ink-soft">Names and sizes only. Workspace content is not visible to the project team.</p>
      <ul className="space-y-1.5 text-sm">
        {(Array.isArray(list) ? list : []).map((s) => <li key={s.id} className="flex justify-between gap-3"><span className="font-bold">{s.name}</span><span>{s.members} users · {new Date(s.created_at).toLocaleDateString("en-GB")}</span></li>)}
      </ul>
    </Card>
  );
};

const ResetLink = () => {
  const [email, setEmail] = useState("");
  const [url, setUrl] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post("/admin/reset-link", { email });
      setUrl(`${window.location.origin}${data.path}`);
    } catch (err) {
      toast.error(errorText(err));
    }
  };
  return (
    <Card title="Password reset for a school user" testid="admin-reset">
      <form onSubmit={submit} className="space-y-3">
        <TextField id="ar-email" label="School user's email" type="email" value={email} onChange={setEmail} testid="admin-reset-email" />
        <button type="submit" className="btn btn-brand btn-sm" data-testid="admin-reset-submit"><KeyRound size={14} aria-hidden /> Create 15-minute reset link</button>
      </form>
      {url && <p className="mt-3 break-all rounded-xl bg-paper p-3 font-mono text-xs" data-testid="admin-reset-url">{url}</p>}
    </Card>
  );
};

const exportCsv = async () => {
  try {
    const res = await api.get("/admin/feedback/export", { responseType: "blob" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(res.data);
    a.download = "feedback-export.csv";
    a.click();
  } catch (err) {
    toast.error(errorText(err));
  }
};

export const AdminInsights = () => (
  <div className="mt-8 space-y-4" data-testid="admin-insights">
    <button type="button" onClick={exportCsv} className="btn btn-ghost btn-sm" data-testid="admin-export-csv"><Download size={14} aria-hidden /> Export feedback (CSV, watermarked)</button>
    <div className="grid gap-4 lg:grid-cols-2">
      <Downloads />
      <Security />
      <Schools />
      <ResetLink />
    </div>
  </div>
);
